import { cn } from '@/platform/lib/utils';
import {
    Description,
    Field,
    Input as HeadlessInput,
    Label,
} from '@headlessui/react';
import { useId } from 'react';

const inputClasses =
    'block w-full border-0 bg-white px-3 py-1.5 text-base text-gray-900 ring-0 placeholder:text-gray-400 read-only:bg-gray-50 read-only:text-gray-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 data-invalid:text-red-900 data-invalid:placeholder:text-red-300 sm:text-sm/6';

const outlineClasses =
    'focus:outline-primary-600 rounded-md outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 disabled:outline-gray-200 data-invalid:outline-red-300 data-invalid:focus:outline-red-600';

const addonClasses =
    'flex shrink-0 items-center bg-gray-50 px-3 text-base text-gray-500 select-none sm:text-sm/6';

const Input = ({
    id,
    label,
    description,
    error,
    required = false,
    type = 'text',
    disabled = false,
    prefix,
    suffix,
    ...props
}) => {
    let uuid = useId();
    let hasAddon = prefix || suffix;

    let input = (
        <HeadlessInput
            {...props}
            id={id || uuid}
            className={cn(
                inputClasses,
                hasAddon ? 'bg-transparent focus:outline-none' : outlineClasses
            )}
            type={type}
            invalid={!!error}
            required={required}
        />
    );

    return (
        <Field disabled={disabled}>
            {label && (
                <Label
                    htmlFor={id || uuid}
                    className='mb-1 block text-sm/6 font-medium text-gray-900'
                >
                    {label}
                    {required && (
                        <span className='inline-block pl-1 text-red-800'>
                            *
                        </span>
                    )}
                </Label>
            )}
            {description && (
                <Description className='mb-1 text-sm/6 text-gray-500'>
                    {description}
                </Description>
            )}
            <div className='grid grid-cols-1'>
                {hasAddon ? (
                    <div
                        className={cn(
                            'focus-within:outline-primary-600 flex w-full overflow-hidden rounded-md bg-white outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2',
                            error &&
                                'outline-red-300 focus-within:outline-red-600',
                            disabled && 'bg-gray-50 outline-gray-200'
                        )}
                    >
                        {prefix && (
                            <span
                                className={cn(
                                    addonClasses,
                                    'border-r border-gray-300'
                                )}
                            >
                                {prefix}
                            </span>
                        )}
                        {input}
                        {suffix && (
                            <span
                                className={cn(
                                    addonClasses,
                                    'border-l border-gray-300'
                                )}
                            >
                                {suffix}
                            </span>
                        )}
                    </div>
                ) : (
                    input
                )}
                {error && (
                    <Description className='mt-1 text-sm text-red-600'>
                        {error}
                    </Description>
                )}
            </div>
        </Field>
    );
};

export default Input;
